import * as Sentry from '@sentry/react';

import router from './router-config';

// Declared in vite.config.ts via define option
declare const RELEASE_NAME: string;
declare const SENTRY_ENVIRONMENT: string;

const isLocalHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
const isLocalDev = import.meta.env.DEV || isLocalHost;
// CDP-driven browsers (Playwright, Puppeteer, Selenium) set this. We skip
// Sentry entirely on them: the short page lifecycle never lets Replay
// upload anyway, and the page-close on test teardown turns in-flight
// fetches into noisy `Failed to fetch` reports.
const isAutomated = typeof navigator !== 'undefined' && navigator.webdriver;

if (!isAutomated && !isLocalDev) {
  Sentry.init({
    dsn: 'https://acef2003d622e4b35e62999232deeeff@o4510759923548160.ingest.us.sentry.io/4510760927952896',
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    enableLogs: true,
    sendDefaultPii: false,
    environment: SENTRY_ENVIRONMENT,
    tracesSampleRate: 1.0,
    // Session Replay stays off globally: student sessions must never be
    // recorded. Replay is started explicitly for CREATOR accounts in
    // useSentryUserEffect once the role is known.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    release: RELEASE_NAME,

    ignoreErrors: [
      // AbortError variants
      /AbortError/i,
      // Unhandled promise rejections with "Timeout"
      /Non-Error promise rejection captured with value:.*Timeout/i,
      // NotAllowedError (but NOT SecurityError — those should stay)
      /NotAllowedError/i,
      'The operation either timed out or was not allowed',
      // Cancelled print requests
      'This print request was cancelled or completed',
      // GraphQL validation and auth errors — filtered by "ErrorType: message" matching
      'GraphQLError: Validation error',
      'GraphQLError: Forbidden',
      // Regex to catch FORBIDDEN ApolloErrors regardless of exact message prefix
      /ApolloError.*Forbidden/i,
      // Tracker/ad-script noise (`Failed to fetch (<host>)` from injected
      // collectors) is deliberately NOT filtered here, and `denyUrls` is gone
      // for the same reason: it matches the last stack frame's URL, which for
      // any fetch failure is our own bundle (Sentry wraps `fetch`, so its
      // instrumentation is always in the stack). Those domains also rotate
      // weekly, so a code-side list is permanently one release behind.
      // Archive such issues in the Sentry stream instead — grouping is per
      // stack trace, so a tracker never shares an issue with our own
      // `Failed to fetch`.
    ],
  });
}
